/**
 * useJobsFromRisk - Fetch jobs related to a specific risk
 *
 * Extracted from useJobs.ts (lines 73-186) for risk-based job filtering
 * Fetches risk, related assets/attributes, and their jobs
 */

import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMy } from '@/hooks/useMy';
import { JobFilters } from '@/sections/jobs/types';
import { parseKeys } from '@/sections/SearchByType';
import { Job } from '@/types';
import { useQuery } from '@/utils/api';

/**
 * Hook for fetching jobs related to a specific risk
 *
 * Multi-step query process:
 * 1. Fetch risk by key
 * 2. Fetch assets/attributes with HAS_VULNERABILITY relationship to risk
 * 3. Build job keys from assets/attributes + risk source
 * 4. Fetch individual jobs by key in parallel
 *
 * @param filter - Job filter configuration with riskKey
 * @param enabled - Whether risk queries should be enabled
 * @returns Jobs related to the specified risk
 */
export function useJobsFromRisk(filter: JobFilters, enabled: boolean) {
  const axios = useAxios();

  // Step 1: Fetch risk by key
  const {
    data: risks,
    status: riskStatus,
    error: riskError,
    isFetching: isFetchingRisk,
    refetch: refetchRisk,
  } = useMy(
    {
      resource: 'risk',
      query: {
        node: {
          labels: ['Risk'],
          filters: [
            { field: 'key', operator: '=', value: filter.riskKey, not: false },
          ],
        },
      },
    },
    { enabled }
  );
  const risk = risks?.[0];

  // Step 2: Fetch assets/attributes with vulnerability relationship
  const {
    data: riskSourcesData,
    status: riskSourcesStatus,
    error: riskSourcesError,
    isFetching: isFetchingRiskSources,
    refetch: refetchRiskSources,
  } = useGenericSearch(
    {
      query: {
        node: {
          relationships: [
            {
              label: ['HAS_VULNERABILITY'],
              target: {
                labels: ['Risk'],
                filters: [
                  {
                    field: 'key',
                    operator: '=',
                    value: filter.riskKey,
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled }
  );

  // Step 3: Build job keys from assets and attributes
  const riskJobKeys = useMemo(() => {
    const updatedRiskJobKeys: string[] = [];

    if (risk) {
      // Build job keys from assets
      if (riskSourcesData?.assets) {
        riskSourcesData.assets.forEach(asset => {
          const jobKey = `#job#${asset.group}#${asset.identifier}#${risk.source}`;
          updatedRiskJobKeys.push(jobKey);
        });
      }

      // Build job keys from attributes
      if (riskSourcesData?.attributes) {
        riskSourcesData.attributes.forEach(attribute => {
          const assetGroup = parseKeys.assetKey(attribute.source).group;

          if (assetGroup) {
            let target = `${attribute.name}://${assetGroup}:${attribute.value}`;
            if (attribute.name === 'protocol') {
              target = `${attribute.value}://${assetGroup}`;
            }
            const jobKey = `#job#${assetGroup}#${target}#${risk.source}`;
            updatedRiskJobKeys.push(jobKey);
          }
        });
      }
    }

    return updatedRiskJobKeys;
  }, [
    riskSourcesData?.assets?.length ?? 0,
    riskSourcesData?.attributes?.length ?? 0,
    risk?.source,
    risk?.key,
  ]);

  // Step 4: Fetch jobs by keys in parallel
  const {
    data: riskJobs = [],
    status: jobsStatus,
    error: riskJobsError,
    isFetching: isFetchingRiskJobs,
    refetch: refetchRiskJobs,
  } = useQuery({
    defaultErrorMessage: 'Failed to fetch risk jobs',
    queryKey: ['riskJobs', riskJobKeys],
    queryFn: async () => {
      const promises = riskJobKeys.map(async key => {
        const jobRes = await axios.get(`/my`, {
          params: {
            label: 'job',
            key: key,
          },
        });
        return jobRes?.data?.jobs?.[0] as Job;
      });

      const res = await Promise.all(promises);
      return res.filter(job => Boolean(job));
    },
    enabled,
    refetchInterval: 5000,
  });

  // Unified refetch for all risk-related queries
  const refetch = () => {
    refetchRisk();
    refetchRiskSources();
    refetchRiskJobs();
  };

  // Aggregate errors
  const error = riskError || riskSourcesError || riskJobsError;

  // Aggregate fetching states
  const isFetching =
    isFetchingRisk || isFetchingRiskSources || isFetchingRiskJobs;

  return {
    data: riskJobs,
    refetch,
    error,
    status: [riskStatus, riskSourcesStatus, jobsStatus],
    isFetching,
    isFetchingNextPage: false, // Risk jobs don't paginate
    fetchNextPage: () => {}, // No pagination for risk jobs
    hasNextPage: false,
  };
}
